import {assert, assertAndContinue} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';

import {
  CoreObject,
  DomainGraphType,
  InterfaceType,
  ObjectType,
  DomainGraphInterface,
} from './CoreObjectConstants';

/**
 * Platform configuration framework
 */

export interface PlatformConfiguration {
  __interfaceType: InterfaceType;
  isPlatformCapabilityEnabledForItemInstance: (instance: CoreObject) => boolean;
}

function getPlatformConfigKey(interfaceType: InterfaceType, itemType: ObjectType): string {
  return `${interfaceType}-${itemType}`;
}

export function getPlatformConfigForItemType(
  interfaceType: InterfaceType,
  itemType: ObjectType
): PlatformConfiguration | undefined {
  return OBJECT_TYPE_PLATFORM_CONFIGURATION_MAP[getPlatformConfigKey(interfaceType, itemType)];
}

// Platform capabilities which are dependent on task schema execution type, mapped to corresponding process platform feature types
const PLATFORM_CAPABILITIES_TO_PROCESS_FEATURE_TYPES: Partial<
  Record<InterfaceType, ProcessPlatformFeatureType>
> = {
  [DomainGraphInterface.STUDYABLE]: ProcessPlatformFeatureType.IS_STUDYABLE,
  [DomainGraphInterface.CORE_OBJECT_ARCHIVABLE]: ProcessPlatformFeatureType.IS_DIRECTLY_ARCHIVEABLE,
  [DomainGraphInterface.CAN_EDIT_WORKFLOW_SCHEMA_FIELD_VALUES]:
    ProcessPlatformFeatureType.CAN_EDIT_SCHEMA_FIELDS,
};

// Processes core object types, mapped to execution types
const PROCESS_DOMAIN_GRAPH_TYPES_TO_EXECUTION_TYPE_MAPPER: {
  [key: string]: (instance: CoreObject) => ProcessExecutionType | null;
} = {
  [DomainGraphType.WORKFLOW_TASK]: (instance) => instance.schema?.executionType,
  [DomainGraphType.WORKFLOW_TASK_GROUP]: (instance) => instance.processTaskSchema?.executionType,
  [DomainGraphType.WORKFLOW_OUTPUT]: (instance) => instance.schema?.processExecutionType,
  [DomainGraphType.WORKFLOW_TASK_SCHEMA]: (instance) => instance.executionType,
  [DomainGraphType.WORKFLOW_OUTPUT_SCHEMA]: (instance) => instance.processExecutionType,
};

// Processes core object types, mapped to task schema names
const PROCESS_DOMAIN_GRAPH_TYPES_TO_SCHEMA_NAME_MAPPER: {
  [key: string]: (instance: CoreObject) => ProcessExecutionType | null;
} = {
  [DomainGraphType.WORKFLOW_TASK]: (instance) => instance.schema?.name,
  [DomainGraphType.WORKFLOW_TASK_GROUP]: (instance) => instance.processTaskSchema?.name,
  [DomainGraphType.WORKFLOW_OUTPUT]: (instance) => instance.schema?.name,
  [DomainGraphType.WORKFLOW_TASK_SCHEMA]: (instance) => instance.name,
  [DomainGraphType.WORKFLOW_OUTPUT_SCHEMA]: (instance) => instance.processTaskSchemaName,
};

function getProcessConfiguration(
  coreObjectType: ObjectType,
  interfaceType: InterfaceType
): PlatformConfiguration {
  return {
    __interfaceType: interfaceType,
    isPlatformCapabilityEnabledForItemInstance: (instance) => {
      const executionType = PROCESS_DOMAIN_GRAPH_TYPES_TO_EXECUTION_TYPE_MAPPER[coreObjectType](instance);
      const processTaskSchemaName =
        PROCESS_DOMAIN_GRAPH_TYPES_TO_SCHEMA_NAME_MAPPER[coreObjectType](instance);
      if (executionType == null || processTaskSchemaName == null) {
        // TODO(haroon): Update all callers to pass schema with execution type
        return true;
      }
      if (
        !assertAndContinue(
          PLATFORM_CAPABILITIES_TO_PROCESS_FEATURE_TYPES[interfaceType] != null,
          'capability must be mapped to process feature type'
        )
      ) {
        return false;
      }
      return getProcessPlatformFeatureConfig(
        PLATFORM_CAPABILITIES_TO_PROCESS_FEATURE_TYPES[interfaceType]!,
        executionType,
        processTaskSchemaName
      );
    },
  };
}

const PROCESS_CONFIGURATION_MAP: Record<string, PlatformConfiguration> = Object.keys(
  PLATFORM_CAPABILITIES_TO_PROCESS_FEATURE_TYPES
).reduce((configurations: Record<string, PlatformConfiguration>, interfaceType: InterfaceType) => {
  for (const coreObjectType of Object.keys(PROCESS_DOMAIN_GRAPH_TYPES_TO_EXECUTION_TYPE_MAPPER)) {
    configurations[getPlatformConfigKey(interfaceType, coreObjectType as ObjectType)] =
      getProcessConfiguration(coreObjectType as ObjectType, interfaceType);
  }
  return configurations;
}, {});

/**
 * Object type platform configurations
 */

export interface StudyableConfiguration extends PlatformConfiguration {
  __interfaceType: typeof DomainGraphInterface.STUDYABLE;
}

export function isStudyableConfiguration(config?: PlatformConfiguration): config is StudyableConfiguration {
  return config?.__interfaceType === DomainGraphInterface.STUDYABLE;
}

export const DEFAULT_STUDYABLE_CONFIGURATION: StudyableConfiguration = {
  __interfaceType: DomainGraphInterface.STUDYABLE,
  isPlatformCapabilityEnabledForItemInstance: () => true,
};

const CONTAINER_STUDYABLE_CONFIGURATION: StudyableConfiguration = {
  __interfaceType: DomainGraphInterface.STUDYABLE,
  isPlatformCapabilityEnabledForItemInstance: (instance: CoreObject) =>
    // This assert is here to prevent silently returning false for a partially hydrated Container instance
    assertAndContinue(
      instance.isFixedPlateWell !== undefined,
      'Item instance must be a Container with isFixedPlateWell defined'
    ) && !instance.isFixedPlateWell,
};

export interface ArchiveableConfiguration extends PlatformConfiguration {
  __interfaceType: typeof DomainGraphInterface.CORE_OBJECT_ARCHIVABLE;
}

export const DEFAULT_ARCHIVEABLE_CONFIGURATION: ArchiveableConfiguration = {
  __interfaceType: DomainGraphInterface.CORE_OBJECT_ARCHIVABLE,
  isPlatformCapabilityEnabledForItemInstance: () => true,
};

/**
 * Object type platform configurations
 */

const OBJECT_TYPE_PLATFORM_CONFIGURATION_MAP: Record<string, PlatformConfiguration> = {
  [getPlatformConfigKey(DomainGraphInterface.STUDYABLE, DomainGraphType.CONTAINER)]:
    CONTAINER_STUDYABLE_CONFIGURATION,
  ...PROCESS_CONFIGURATION_MAP,
};

export function getPlatformConfiguration<TInterfaceType extends InterfaceType>(
  interfaceType: TInterfaceType,
  itemType: ObjectType,
  defaultConfiguration: PlatformConfiguration & {__interfaceType: TInterfaceType}
): PlatformConfiguration {
  const maybeConfig = getPlatformConfigForItemType(interfaceType, itemType);
  if (maybeConfig == null) {
    return defaultConfiguration;
  }
  assert(
    maybeConfig.__interfaceType === interfaceType,
    `Expected platform configuration to be for capability ${interfaceType} got ${maybeConfig.__interfaceType}`,
    {maybeConfig}
  );

  return maybeConfig;
}
